<template>
  <div class="request-table" data-testid="request-table">
    <v-card-text v-if="loading" data-testid="request-table__loading">
      <v-skeleton-loader type="table-heading, list-item-two-line, article" />
    </v-card-text>

    <div v-else class="request-table__table-area">
      <div class="request-table__table-panel">
        <lb-datatableaction
          v-if="requestList.length > 0 || originalRequestCount > 0"
          ref="dataTableAction"
          :headers="resolvedHeaders"
          :tabledata="requestList"
          :original-data="originalRequestList"
          :filter-options-data="columnFilterOptionsData"
          :itemsperpage="itemsPerPage"
          :searchterm="searchQuery"
          :table-height="tableHeight || undefined"
          :displayed-row-count="paginatedItems.length"
          :is-data-selected="isPartialSelection"
          :checkalldata="isAllSelected"
          :selectfilter="selectFilter"
          :page_details="pageDetails"
          header-bg-class="fbc-table-header-bg"
          data-testid="request-table__datatable"
          @select-all-change="onSelectAllChange"
          @filter-item="onFilterItem"
          @filter-active-change="onFilterActiveChange"
          @clear-item="onClearItem"
          @column-reorder="handleColumnReorder"
        >
          <template v-slot:tbody="{ headers: slotHeaders }">
            <tr
              v-for="(item, index) in paginatedItems"
              :key="item._id || index"
              class="cursor-pointer"
              :class="{ 'low-opacity': item.status === false }"
              :data-testid="`request-table__row--${index}`"
              :data-request-id="item._id"
              @mouseenter="onRowMouseEnter($event, item)"
              @mouseleave="onRowMouseLeave"
              @focusin="onRowFocusIn($event, item)"
              @focusout="onRowFocusOut"
            >
              <td class="px-4 text-center checkbox-cell" @click.stop>
                <v-checkbox
                  dense
                  color="caccounting"
                  class="ma-0 py-1 single-line"
                  :ripple="false"
                  hide-details
                  :input-value="isRequestSelected(item._id)"
                  :data-testid="`request-table__row-checkbox--${index}`"
                  @change="onRowSelectChange(item._id, $event)"
                />
              </td>

              <template v-for="header in slotHeaders.filter((column) => column.value !== 'action')">
                <td
                  v-if="header.visible"
                  :key="`${header.value}-${item._id || index}`"
                  class="single-line"
                  :class="{
                    'description-header': header.value === 'description',
                    'request-table__ref-col': header.value === 'ref',
                  }"
                  :data-column-value="header.value"
                  :data-testid="`request-table__cell--${header.value}-row-${index}`"
                  @click="onCellClick($event, item, header)"
                >
                  <span v-if="header.value === 'index'">
                    {{ resolveRequestSerialNumber(item, (currentPage - 1) * itemsPerPage + index + 1) }}
                  </span>

                  <button
                    v-else-if="header.value === 'description'"
                    type="button"
                    class="request-table__description-link single-line"
                    :data-testid="`request-table__description-link--${index}`"
                    @click.stop="$emit('navigate-detail', item)"
                  >
                    {{ item.description }}
                  </button>

                  <div v-else-if="header.value === 'stage'" class="d-flex align-center">
                    <StatusIndicator
                      v-if="getStageStatusVariant(item.stage)"
                      :variant="getStageStatusVariant(item.stage)"
                      :custom-label="getStageStatusLabel(item.stage)"
                      size="sm"
                      outlined
                    />
                  </div>

                  <div v-else-if="header.value === 'priority'" class="d-flex align-center">
                    <TaskPriorityDisplay
                      v-if="item.priority"
                      :priority="item.priority"
                      :data-test-id="`request-table__priority-cell-${index}`"
                    />
                  </div>

                  <span v-else-if="header.value === 'info_owner_name'">
                    {{ (item.info_owner_detail || {}).name || (item.info_owner_detail || {}).email }}
                  </span>

                  <span v-else-if="header.value === 'created_by_name'">
                    {{ item.created_by_name || 'N/A' }}
                  </span>

                  <span v-else-if="header.value === 'ref'" class="request-table__ref-cell">
                    {{ formatReferenceChecklist(item) }}
                  </span>

                  <div v-else-if="header.value === 'duedate_days'" class="d-flex align-center">
                    <TaskDueDateCell
                      v-if="item.duedate"
                      days-only
                      :due-date="item.duedate"
                      :data-test-id="`request-table__due-days-${index}`"
                    />
                  </div>

                  <span v-else-if="header.value === 'location'">
                    {{ getLocationName(item.location) }}
                  </span>

                  <span v-else-if="header.value === 'type'">
                    {{ getTypeLabel(item.type) }}
                  </span>

                  <span v-else-if="header.value === 'created_at'">
                    {{ $nova.formatDateTime(new Date(item.created_at), 'DD MMM yyyy') }}
                  </span>

                  <span v-else-if="header.value === 'updated_at'">
                    {{ $nova.formatDateTime(new Date(item.updated_at), 'DD MMM yyyy') }}
                  </span>

                  <span v-else>{{ item[header.value] }}</span>
                </td>
              </template>
            </tr>
          </template>
        </lb-datatableaction>

        <div
          v-show="showHoverActionsOverlay"
          ref="hoverOverlay"
          class="request-table__hover-overlay"
          role="toolbar"
          :aria-label="activeHoverRequest ? getRowActionsAriaLabel(activeHoverRequest) : 'Row actions'"
          :style="hoverOverlayStyle"
          @mouseenter="onOverlayMouseEnter"
          @mouseleave="onOverlayMouseLeave"
        >
          <RequestRowHoverActions
            v-if="activeHoverRequest"
            :request="activeHoverRequest"
            :primary-actions="getPrimaryActions(activeHoverRequest)"
            :overflow-actions="getOverflowActions(activeHoverRequest)"
            :overflow-open="openOverflowMenuRequestId === activeHoverRequest._id"
            @action="onRowAction"
            @overflow-toggle="onOverflowToggle(activeHoverRequest._id, $event)"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import './RequestTable.scss';
import StatusIndicator from '@/components/StatusIndicator.vue';
import { TaskDueDateCell, TaskPriorityDisplay } from '../../../TaskList/components';
import RequestRowHoverActions from './RequestRowHoverActions.vue';
import { getRequestActions } from '../../utils/requestActions';
import {
  INFORMATION_MANAGER_STAGE_LABEL_MAP,
  INFORMATION_MANAGER_STAGE_STATUS_VARIANT,
} from '../../../../../utils/constants';
import {
  resolveLocationName,
  resolveRequestSerialNumber,
  formatReferenceChecklist,
} from '../../../../../utils/informationManager';
import {
  resolveResponseTypeLabel,
  downloadAttachment as downloadRequestAttachment,
  downloadAllAttachments as downloadAllRequestAttachments,
} from '../../utils/requestDetail';

export default {
  name: 'RequestTable',
  components: {
    StatusIndicator,
    TaskDueDateCell,
    TaskPriorityDisplay,
    RequestRowHoverActions,
  },
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    paginatedItems: {
      type: Array,
      default: () => [],
    },
    requestList: {
      type: Array,
      default: () => [],
    },
    originalRequestList: {
      type: Array,
      default: () => [],
    },
    originalRequestCount: {
      type: Number,
      default: 0,
    },
    filterOptionsList: {
      type: Array,
      default: null,
    },
    headers: {
      type: Array,
      default: () => [],
    },
    itemsPerPage: {
      type: Number,
      default: 12,
    },
    currentPage: {
      type: Number,
      default: 1,
    },
    selectedRequestIds: {
      type: Array,
      default: () => [],
    },
    selectFilter: {
      type: Object,
      default: () => ({}),
    },
    pageDetails: {
      type: Object,
      default: () => ({}),
    },
    locationList: {
      type: Array,
      default: () => [],
    },
    searchQuery: {
      type: String,
      default: '',
    },
    tableHeight: {
      type: String,
      default: '',
    },
    canEdit: {
      type: Boolean,
      default: true,
    },
    canDelete: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      hasActiveTableFilter: false,
      hoveredRequest: null,
      hoveredRowEl: null,
      hoverOverlayStyle: {},
      overlayHoverLock: false,
      openOverflowMenuRequestId: null,
      hoverOverlayRafId: null,
      tableScrollEl: null,
    };
  },
  computed: {
    locationFilterLabelMap() {
      const map = {};
      (this.locationList || []).forEach((location) => {
        if (location?._id == null) return;
        map[String(location._id)] = location.name || String(location._id);
      });
      return map;
    },
    resolvedHeaders() {
      const locationLabelMap = this.locationFilterLabelMap;
      return this.headers.map((header) => {
        if (header.value !== 'location') return header;
        return { ...header, filterLabelMap: locationLabelMap };
      });
    },
    visibleHeaders() {
      return this.headers.filter((header) => header.value !== 'action');
    },
    columnFilterOptionsData() {
      return this.filterOptionsList?.length ? this.filterOptionsList : null;
    },
    activeHoverRequest() {
      if (this.openOverflowMenuRequestId) {
        return this.paginatedItems.find((req) => req._id === this.openOverflowMenuRequestId)
          || this.hoveredRequest;
      }
      return this.hoveredRequest;
    },
    showHoverActionsOverlay() {
      const request = this.activeHoverRequest;
      if (!request) return false;
      const { primary, overflow } = getRequestActions(request, {
        surface: 'list',
        canEdit: this.canEdit,
        canDelete: this.canDelete,
      });
      return primary.length > 0 || overflow.length > 0;
    },
    isAllSelected() {
      const ids = (this.requestList || []).map((item) => item._id).filter(Boolean);
      if (!ids.length || !this.selectedRequestIds.length) return false;
      const selectedSet = new Set(this.selectedRequestIds.map((id) => String(id)));
      return ids.every((id) => selectedSet.has(String(id)));
    },
    isPartialSelection() {
      return this.selectedRequestIds.length > 0 && !this.isAllSelected;
    },
  },
  watch: {
    paginatedItems() {
      this.$nextTick(() => {
        this.$refs.dataTableAction?.scheduleTruncationRefresh();
      });
    },
    showHoverActionsOverlay(visible) {
      if (visible) {
        this.bindTableScrollListener();
        this.scheduleHoverOverlayPositionUpdate();
      } else {
        this.unbindTableScrollListener();
      }
    },
  },
  beforeDestroy() {
    this.unbindTableScrollListener();
  },
  methods: {
    getListViewSnapshot() {
      return {
        tableFilterHeader: { ...(this.selectFilter || {}) },
        itemsPerPage: this.itemsPerPage,
      };
    },
    applyListViewSnapshot(partial = {}) {
      if (partial.tableFilterHeader) {
        this.$emit('update:selectFilter', { ...partial.tableFilterHeader });
      }
      if (typeof partial.itemsPerPage === 'number' && partial.itemsPerPage > 0) {
        this.$emit('update:itemsPerPage', partial.itemsPerPage);
      }
    },
    clearTableFilter() {
      this.$refs.dataTableAction?.clearFilter();
    },
    getStageStatusVariant(stage) {
      return INFORMATION_MANAGER_STAGE_STATUS_VARIANT[stage] || null;
    },
    getStageStatusLabel(stage) {
      return INFORMATION_MANAGER_STAGE_LABEL_MAP[String(stage)] || '';
    },
    getLocationName(locationId) {
      return resolveLocationName(locationId, this.locationList);
    },
    resolveRequestSerialNumber,
    formatReferenceChecklist,
    getTypeLabel(type) {
      return resolveResponseTypeLabel(type);
    },
    getPrimaryActions(request) {
      return getRequestActions(request, {
        surface: 'list',
        canEdit: this.canEdit,
        canDelete: this.canDelete,
      }).primary;
    },
    getOverflowActions(request) {
      return getRequestActions(request, {
        surface: 'list',
        canEdit: this.canEdit,
        canDelete: this.canDelete,
      }).overflow;
    },
    isRequestSelected(requestId) {
      return this.selectedRequestIds.some((id) => String(id) === String(requestId));
    },
    onRowSelectChange(requestId, selected) {
      const targetId = String(requestId);
      const next = selected
        ? [...this.selectedRequestIds, requestId]
        : this.selectedRequestIds.filter((id) => String(id) !== targetId);
      this.$emit('update:selectedRequestIds', next);
    },
    onSelectAllChange(selectAll) {
      if (selectAll) {
        this.$emit('update:selectedRequestIds', this.requestList.map((item) => item._id));
      } else {
        this.$emit('update:selectedRequestIds', []);
      }
    },
    onFilterItem(item, header) {
      this.$emit('filter-item', item, header);
    },
    onClearItem(item) {
      this.$emit('clear-item', item);
    },
    onFilterActiveChange(isActive) {
      this.hasActiveTableFilter = isActive;
      this.$emit('filter-active-change', isActive);
    },
    handleColumnReorder(headers) {
      this.$emit('update:headers', headers);
    },
    onCellClick(event, item, header) {
      if (header.value === 'description') return;
      this.$emit('navigate-detail', item);
    },
    onRowAction({ actionId, request }) {
      this.$emit('row-action', { actionId, request });
      this.openOverflowMenuRequestId = null;
    },
    onOverflowToggle(requestId, isOpen) {
      this.openOverflowMenuRequestId = isOpen ? requestId : null;
      if (isOpen) {
        this.scheduleHoverOverlayPositionUpdate();
      }
    },
    getRowActionsAriaLabel(item) {
      const name = (item?.description || '').trim() || 'request';
      return `Row actions for ${name}`;
    },
    onRowMouseEnter(event, item) {
      this.activateRowActions(event.currentTarget, item);
    },
    onRowMouseLeave(event) {
      this.deactivateRowActions(event, event.currentTarget);
    },
    onRowFocusIn(event, item) {
      this.activateRowActions(event.currentTarget, item);
    },
    onRowFocusOut(event) {
      this.deactivateRowActions(event, event.currentTarget);
    },
    activateRowActions(rowEl, item) {
      if (!rowEl) return;
      if (this.openOverflowMenuRequestId && this.openOverflowMenuRequestId !== item._id) {
        this.openOverflowMenuRequestId = null;
      }
      this.hoveredRequest = item;
      this.hoveredRowEl = rowEl;
      this.updateHoverOverlayPosition();
    },
    shouldKeepRowActionsOpen(relatedTarget, rowEl) {
      if (!relatedTarget) return false;
      if (this.$refs.hoverOverlay?.contains(relatedTarget)) return true;
      if (rowEl?.contains(relatedTarget)) return true;
      return false;
    },
    deactivateRowActions(event, rowEl) {
      if (this.openOverflowMenuRequestId || this.overlayHoverLock) return;
      const related = event?.relatedTarget;
      if (this.shouldKeepRowActionsOpen(related, rowEl)) return;
      this.clearHoverState();
    },
    onOverlayMouseEnter() {
      this.overlayHoverLock = true;
    },
    onOverlayMouseLeave(event) {
      this.overlayHoverLock = false;
      this.deactivateRowActions(event, this.hoveredRowEl);
    },
    clearHoverState() {
      this.hoveredRequest = null;
      this.hoveredRowEl = null;
      this.hoverOverlayStyle = {};
    },
    getTableScrollWrapper() {
      return this.$el?.querySelector('.v-data-table__wrapper')
        || this.$el?.querySelector('.lb-datatableaction-wrapper');
    },
    bindTableScrollListener() {
      const wrapper = this.getTableScrollWrapper();
      if (!wrapper || this.tableScrollEl === wrapper) return;
      this.unbindTableScrollListener();
      this.tableScrollEl = wrapper;
      wrapper.addEventListener('scroll', this.onTableViewportChange, { passive: true });
      window.addEventListener('resize', this.onTableViewportChange, { passive: true });
      window.addEventListener('scroll', this.onTableViewportChange, { passive: true });
    },
    unbindTableScrollListener() {
      this.cancelHoverOverlayPositionUpdate();
      if (this.tableScrollEl) {
        this.tableScrollEl.removeEventListener('scroll', this.onTableViewportChange);
      }
      window.removeEventListener('resize', this.onTableViewportChange);
      window.removeEventListener('scroll', this.onTableViewportChange);
      this.tableScrollEl = null;
    },
    cancelHoverOverlayPositionUpdate() {
      if (this.hoverOverlayRafId != null) {
        cancelAnimationFrame(this.hoverOverlayRafId);
        this.hoverOverlayRafId = null;
      }
    },
    onTableViewportChange() {
      if (!this.showHoverActionsOverlay || this.hoverOverlayRafId != null) return;
      this.hoverOverlayRafId = requestAnimationFrame(() => {
        this.hoverOverlayRafId = null;
        if (this.showHoverActionsOverlay) {
          this.updateHoverOverlayPosition();
        }
      });
    },
    scheduleHoverOverlayPositionUpdate() {
      this.$nextTick(() => this.updateHoverOverlayPosition());
    },
    updateHoverOverlayPosition() {
      let rowEl = this.hoveredRowEl;
      if (!rowEl && this.activeHoverRequest?._id) {
        rowEl = this.$el.querySelector(`[data-request-id="${this.activeHoverRequest._id}"]`);
      }
      const wrapper = this.getTableScrollWrapper();
      if (!rowEl || !wrapper) {
        this.openOverflowMenuRequestId = null;
        this.overlayHoverLock = false;
        this.clearHoverState();
        return;
      }
      const wrapperRect = wrapper.getBoundingClientRect();
      const rowRect = rowEl.getBoundingClientRect();
      const isOutOfView = rowRect.bottom <= wrapperRect.top
        || rowRect.top >= wrapperRect.bottom
        || rowRect.right <= wrapperRect.left
        || rowRect.left >= wrapperRect.right;
      if (isOutOfView) {
        this.openOverflowMenuRequestId = null;
        this.overlayHoverLock = false;
        this.clearHoverState();
        return;
      }
      const overlayHeight = this.$refs.hoverOverlay?.offsetHeight || 40;
      const edgeOffset = 12;
      const minTop = wrapperRect.top + 4;
      const maxTop = wrapperRect.bottom - overlayHeight - 4;
      const idealTop = rowRect.top + (rowRect.height - overlayHeight) / 2;
      const top = Math.max(minTop, Math.min(idealTop, maxTop));
      this.hoverOverlayStyle = {
        top: `${top}px`,
        left: `${wrapperRect.right - edgeOffset}px`,
      };
    },
    downloadAttachment(file) {
      downloadRequestAttachment(this.$nova, file);
    },
    downloadAllAttachments(row) {
      downloadAllRequestAttachments(this.$nova, row);
    },
  },
};
</script>
